import { useMemo, useState } from 'react';

import { colorForId, initialsOf, pluralize } from '~/client/lib/format';
import { formatDayLabel, formatRangeLabel, monthsInRange } from '~/shared/dates';
import { buildDensity, everyoneFreeRuns, topOverlap } from '~/shared/density';
import type { PollWithRespondents, Respondent } from '~/shared/types';
import { nowIsoDay } from '~/shared/tz';

import { Calendar } from './Calendar';
import { DayBreakdown, DayPreview } from './DayBreakdown';
import { Icon } from './Icons';
import { Avatar, Button, Chip } from './Primitives';

interface ResultViewProps {
  data: PollWithRespondents;
  allRespondents: Respondent[];
  excludedIds: Set<string>;
  onToggleExcluded: (id: string) => void;
  onResetExcluded: () => void;
  selfRespondentId: string;
  onEdit: () => void;
  onShare: () => void;
}

export function ResultView({
  data,
  allRespondents,
  excludedIds,
  onToggleExcluded,
  onResetExcluded,
  selfRespondentId,
  onEdit,
  onShare,
}: ResultViewProps) {
  const { poll, respondents } = data;
  const total = respondents.length;
  const density = useMemo(() => buildDensity(respondents), [respondents]);
  const top = useMemo(() => topOverlap(density, 8), [density]);
  const runs = useMemo(() => (total > 0 ? everyoneFreeRuns(density, total) : []), [density, total]);
  const sortedRuns = useMemo(
    () =>
      [...runs].sort((a, b) => {
        if (b.days.length !== a.days.length) return b.days.length - a.days.length;
        return a.start < b.start ? -1 : 1;
      }),
    [runs],
  );
  const longestRun = sortedRuns[0];
  const otherRuns = sortedRuns.slice(1);

  const months = monthsInRange(poll.startDate, poll.endDate);
  const firstMonth = months[0] ?? poll.startDate.slice(0, 7);
  const [year, setYear] = useState(() => Number(firstMonth.slice(0, 4)));
  const [month, setMonth] = useState(() => Number(firstMonth.slice(5, 7)) - 1);
  const [breakdownDay, setBreakdownDay] = useState<string | null>(null);
  const todayIso = nowIsoDay(poll.timeZone);

  const me = allRespondents.find((r) => r.id === selfRespondentId);
  const hiddenCount = excludedIds.size;

  return (
    <main className="app-main">
      <div className="container container-narrow">
        <div className="row" style={{ gap: 8, marginBottom: 12 }}>
          <Chip variant="lime" dot>
            you're in
          </Chip>
          <span className="mono muted" style={{ fontSize: 12 }}>
            /{poll.id}
          </span>
        </div>
        <h1 className="page-title-l">
          {longestRun ? (
            <>
              you're <span className="hl">all free</span>{' '}
              {formatRangeLabel(longestRun.start, longestRun.end)}.
            </>
          ) : top.length > 0 ? (
            <>
              best so far: <span className="hl">{formatDayLabel(top[0]!.day)}</span>
            </>
          ) : (
            <>no overlap yet.</>
          )}
        </h1>
        {otherRuns.length > 0 && (
          <p style={{ fontSize: 16, color: 'var(--fg-muted)', marginTop: 8, marginBottom: 0 }}>
            also{' '}
            {otherRuns.map((r, i) => {
              const sep =
                i === otherRuns.length - 1 ? '' : i === otherRuns.length - 2 ? ' and ' : ', ';
              return (
                <span key={r.start}>
                  <strong style={{ color: 'var(--ink)', fontWeight: 500 }}>
                    {formatRangeLabel(r.start, r.end)}
                  </strong>
                  {sep}
                </span>
              );
            })}
            .
          </p>
        )}
        <p style={{ fontSize: 18, color: 'var(--fg-muted)', marginTop: 12, marginBottom: 16 }}>
          {total <= 1
            ? 'when more mates reply, the brightest cells are when everyone’s free.'
            : `${total} ${pluralize(total, 'reply', 'replies')}${
                hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ''
              } so far.`}
        </p>
        {hiddenCount > 0 && (
          <div className="exclusion-banner row between row-wrap" style={{ marginBottom: 16 }}>
            <span>
              showing {total} of {allRespondents.length} · <strong>{hiddenCount} hidden</strong>
            </span>
            <button type="button" className="btn btn-text btn-sm" onClick={onResetExcluded}>
              show everyone
            </button>
          </div>
        )}

        <div className="card-surface" style={{ padding: 20, marginBottom: 16 }}>
          <div className="field-label">ranked overlap</div>
          {top.length === 0 ? (
            <p className="muted" style={{ marginTop: 12, marginBottom: 0 }}>
              {allRespondents.length === 0
                ? 'no overlap yet — share the link with a few more people.'
                : 'no overlap with this group.'}
            </p>
          ) : (
            <div className="col" style={{ gap: 4, marginTop: 12 }}>
              {top.map(({ day, yes, maybe, weighted }, i) => {
                const isTop = yes === total;
                return (
                  <button
                    key={day}
                    type="button"
                    className="overlap-row"
                    onClick={() => setBreakdownDay(day)}
                    style={{
                      background: isTop ? 'rgba(200,255,74,.4)' : 'transparent',
                    }}
                  >
                    <span className="mono muted" style={{ fontSize: 12, width: 24 }}>
                      #{i + 1}
                    </span>
                    <span style={{ fontSize: 16, fontWeight: 500, flex: 1 }}>
                      {formatDayLabel(day)}
                    </span>
                    <span className="mono" style={{ fontSize: 13 }}>
                      {yes}
                      {maybe > 0 ? `+${maybe}?` : ''}/{total}
                    </span>
                    <div className="overlap-bar">
                      <div
                        className="overlap-bar-fill"
                        style={{ width: `${(weighted / Math.max(total, 1)) * 100}%` }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="card-surface" style={{ padding: 20, marginBottom: 16 }}>
          <div className="row between" style={{ marginBottom: 12 }}>
            <div className="field-label" style={{ marginBottom: 0 }}>
              everyone's calendar
            </div>
            <div className="density-legend">
              <span>fewer</span>
              <span className="legend-cell" style={{ background: 'rgba(200,255,74,.18)' }} />
              <span className="legend-cell" style={{ background: 'var(--lime)' }} />
              <span>everyone</span>
            </div>
          </div>
          <Calendar
            year={year}
            month={month}
            density={density}
            total={Math.max(total, 1)}
            onMonthChange={(y, m) => {
              setYear(y);
              setMonth(m);
            }}
            mode="view"
            todayIso={todayIso}
            windowStart={poll.startDate}
            windowEnd={poll.endDate}
            onCellClick={setBreakdownDay}
            renderCellHover={
              respondents.length > 0
                ? (day) => <DayPreview day={day} respondents={respondents} />
                : undefined
            }
          />
          <div className="muted" style={{ fontSize: 12, marginTop: 12 }}>
            hover or tap a day to see who's in.
          </div>
        </div>

        {allRespondents.length > 0 && (
          <div className="card-surface" style={{ padding: 20, marginBottom: 16 }}>
            <div className="row between" style={{ marginBottom: 12 }}>
              <div className="field-label" style={{ marginBottom: 0 }}>
                who's replied
              </div>
              <span className="mono muted" style={{ fontSize: 11 }}>
                {total} of {allRespondents.length}
              </span>
            </div>
            <div className="col" style={{ gap: 6 }}>
              {allRespondents.map((r) => {
                const hidden = excludedIds.has(r.id);
                return (
                  <div
                    key={r.id}
                    className={`respondent-row${hidden ? ' respondent-row-hidden' : ''}`}
                  >
                    <Avatar initials={initialsOf(r.name)} color={colorForId(r.id)} size={28} />
                    <span className="name">
                      {r.name}
                      {r.id === selfRespondentId && (
                        <span className="muted" style={{ marginLeft: 6, fontSize: 12 }}>
                          (you)
                        </span>
                      )}
                    </span>
                    <span className="mono muted" style={{ fontSize: 12 }}>
                      {r.yes.length}
                      {r.maybe.length > 0 ? `+${r.maybe.length}?` : ''}{' '}
                      {pluralize(r.yes.length, 'day')}
                    </span>
                    <button
                      type="button"
                      className="btn btn-text btn-sm respondent-toggle"
                      onClick={() => onToggleExcluded(r.id)}
                      aria-label={hidden ? `include ${r.name}` : `hide ${r.name}`}
                    >
                      {hidden ? 'include' : 'hide'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="sticky-actions">
          <Button variant="ghost" onClick={onEdit}>
            <Icon name="edit" size={14} /> edit my dates
          </Button>
          <Button variant="primary" onClick={onShare}>
            <Icon name="share" size={14} /> share
          </Button>
        </div>
        {me && me.yes.length === 0 && me.maybe.length === 0 && (
          <p className="muted" style={{ fontSize: 13, marginTop: 12 }}>
            you haven't picked any days yet — tap edit to add them.
          </p>
        )}
      </div>
      {breakdownDay && (
        <DayBreakdown
          day={breakdownDay}
          respondents={respondents}
          selfRespondentId={selfRespondentId}
          onClose={() => setBreakdownDay(null)}
        />
      )}
    </main>
  );
}
